import {
  combineLatest,
  distinctUntilChanged,
  mergeMap,
  take,
  throttleTime,
} from 'rxjs';
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { uniq } from 'lodash';

import { useMetaStore } from '@apps/main/new-desktop/core/stores';

import { assertValue, performanceMark, logged } from '@libs/utils';
import type {
  FlightIngredientResponse,
  FerryIngredientResponse,
  PortStaticInfo,
  HotelIngredientResponse,
  ItinerarySearchUserCriteria,
  BusinessCriteria,
  ItineraryWithPrice,
  SelectedTicketInfo,
  TransportLeg,
  OwnStayIngredientResponse,
  DestinationSuggestedActivitiesSearchCriteria,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';
import {
  isAvailableActivityIngredientResponse,
  isAvailableFlightIngredientResponse,
  isAvailableTransportIngredientResponse,
} from '@libs/search-service-smart-search';
import { trackReisegigantenNoAvailability } from '@libs/google-analytics-events';

import type TripPlannerModel from '../tripPlannerModel/TripPlannerModel';
import { getFormattedTime } from '../helpers/formatting';
import { asObservable } from '../helpers/rx-vue';
import { flatIatas } from '../recipe/ingredients/helpers';
import { isReisegiganten } from '../helpers/metas';
import type { CriteriaState } from '../state/criteriaState';
import type { RecipeState } from '../state/recipeState';
import type { TripState } from '../state/tripState';
import { useItineraryStore } from '../store/itineraryStore';
import { usePreferredTimeStore } from '../store/preferredTimeStore';
import { useEditingIngredientStore } from '../store/editingIngredientStore';
import {
  isHotelIngredientAdapter,
  isOwnStayIngredientAdapter,
} from '../recipe/ingredients/typeGuards';
import type { HotelIngredient } from '../recipe/ingredients';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useItineraryViewModel(
  recipeState: RecipeState,
  tripState: TripState,
  criteriaState: CriteriaState,
  model: TripPlannerModel
) {
  const itineraryStore = useItineraryStore();
  const { selectedItinerary } = storeToRefs(itineraryStore);
  const preferredTimeStore = usePreferredTimeStore();
  const editingIngredientStore = useEditingIngredientStore();
  const metaStore = useMetaStore();

  const shouldSkipNextItinerarySearch = ref<boolean>();

  function searchItinerariesFromCriteria(
    itineraryCriteria: ItinerarySearchUserCriteria,
    businessCriteria: BusinessCriteria,
    selectedDate?: Date
  ): void {
    if (
      editingIngredientStore.editingStayIngredient?.userIsTyping ||
      !recipeState.isRecipeCorrect.value
    ) {
      return;
    }
    // NOTE: since isEditingTripType is defined as false, it should not affect normal flow
    if (tripState.isEditingTripType.value) {
      return;
    }

    if (recipeState.shouldSkipItinerarySearch.value) {
      recipeState.shouldSkipItinerarySearch.value = false;
      return;
    }

    //TODO remove this logic as soon as we stop setting number of nights after being suggested
    if (shouldSkipNextItinerarySearch.value) {
      shouldSkipNextItinerarySearch.value = false;
      return;
    }

    shouldSkipNextItinerarySearch.value =
      shouldCriteriaSkipNextIteration(itineraryCriteria);

    model.searchItineraries(itineraryCriteria, businessCriteria, selectedDate);
  }

  watch(
    [criteriaState.itineraryCriteria, tripState.isEditingTripType],
    () => {
      // NOTE: since isEditingTripType is defined as false, it should not affect normal flow
      if (!tripState.isEditingTripType.value) {
        searchItinerariesFromCriteria(
          criteriaState.itineraryCriteria.value,
          criteriaState.businessCriteria.value,
          preferredTimeStore.preferredDay
        );
      }
    },
    { deep: true }
  );

  watch(
    criteriaState.destinationSuggestedActivitiesCriteria,
    (criteria) => {
      searchDestinationSuggestedActivitiesFromCriteria(criteria);
    },
    { deep: true, immediate: true }
  );

  function searchDestinationSuggestedActivitiesFromCriteria(
    criteria: DestinationSuggestedActivitiesSearchCriteria
  ): void {
    criteria.destinations.map(({ constraint, ingredientId }) =>
      model
        .searchDestinationSuggestedActivities(constraint)
        .subscribe((suggestedActivitiesStaticInfo) =>
          itineraryStore.insertDestinationSuggestedActivities(
            suggestedActivitiesStaticInfo,
            ingredientId
          )
        )
    );
  }

  // NOTE: Disable automatic activity suggestions search to check if this piece of code was responsible for
  //       reported incident issues around activity availability search response times
  //       https://semboteam.slack.com/archives/C0852F0THHS/p1734340291771089

  // watch(
  //   criteriaState.activitySuggestionsCriteria,
  //   (criteria) => searchActivitySuggestionsFromCriteria(criteria),
  //   { immediate: true }
  // );

  // function searchActivitySuggestionsFromCriteria(
  //   criterias: ActivitySuggestionsSearchCriteria[]
  // ): void {
  //   criterias.forEach((criteria) => {
  //     model
  //       .getSuggestedActivities(criteria)
  //       .pipe(
  //         mergeMap((suggestions) => {
  //           const codes = suggestions.suggestions.map(
  //             (suggestion) => suggestion.code
  //           );
  //           return model.getActivitiesStaticData(codes).pipe(
  //             map(
  //               (
  //                 staticData: ActivityStaticInfo[]
  //               ): ActivitySuggestionsByIngredientId | null => {
  //                 const ingredientId =
  //                   criteria.userCriteria.recipe.ingredients?.[
  //                     suggestions.ingredientIndex
  //                   ]?.ingredientId;

  //                 if (ingredientId) {
  //                   const activitySuggestionsDictionary: ActivitySuggestionsByIngredientId =
  //                     {
  //                       [ingredientId]: [],
  //                     };

  //                   staticData.forEach((data) => {
  //                     const suggestion = suggestions.suggestions.find(
  //                       (val) => val.code === data.hotelCode
  //                     );
  //                     if (suggestion) {
  //                       activitySuggestionsDictionary[ingredientId]?.push({
  //                         ...suggestion,
  //                         name: data.name,
  //                         images: data.images,
  //                       });
  //                     }
  //                   });

  //                   return activitySuggestionsDictionary;
  //                 }

  //                 return null;
  //               }
  //             )
  //           );
  //         })
  //       )
  //       .subscribe(itineraryStore.insertActivitySuggestions);
  //   });
  // }

  function shouldCriteriaSkipNextIteration(
    criteria: ItinerarySearchUserCriteria
  ): boolean {
    return !!criteria.ingredients?.some(
      (ingredient) =>
        (isHotelIngredientAdapter(ingredient) ||
          isOwnStayIngredientAdapter(ingredient)) &&
        !ingredient.numberOfNights
    );
  }

  const searchedCarrierNames = new Set<string>();

  model.itinerarySearches.subscribe(() => {
    itineraryStore.itinerarySearchCounter++;
    itineraryStore.staticItinerarySearchCounter++;

    searchedCarrierNames.clear();

    const recipeCarrierFilterCodes = uniq(
      recipeState.flightOnlyRecipe.value.flatMap(
        (flightIngredient) => flightIngredient.filters.carrierFilters
      )
    );

    recipeCarrierFilterCodes.forEach((carrierCode) =>
      searchedCarrierNames.add(carrierCode)
    );

    model
      .searchCarrierNames(recipeCarrierFilterCodes)
      .subscribe((carrierNames) => {
        tripState.setCarrierNames(carrierNames);
      });
  });

  const firstItineraryReceived = model.itinerarySearchResults.pipe(
    mergeMap((results) => results.itineraries.pipe(take(1)))
  );
  firstItineraryReceived.subscribe(() =>
    performanceMark('FirstItineraryReceived')
  );

  model.itinerarySearchResults.pipe(take(1)).subscribe((result) =>
    result.itineraries.subscribe({
      complete: () => {
        if (
          metaStore.metaAffiliateId &&
          isReisegiganten(metaStore.metaAffiliateId) &&
          !itineraryStore.selectedItinerary
        ) {
          trackReisegigantenNoAvailability();
        }
      },
    })
  );

  model.itinerarySearchResults.subscribe((result) => {
    tripState.clearOverviewStaticInfo();
    itineraryStore.clearItineraries();

    result.itineraries.pipe(take(1)).subscribe((itinerary) => {
      if (!preferredTimeStore.isDateInExpandedMonth(itinerary.date)) {
        preferredTimeStore.expandMonth(itinerary.date);
      }
    });

    result.itineraries.subscribe({
      next: (itinerary) => {
        itineraryStore.insertItinerary(itinerary);

        const allCarrierCodes = uniq(
          itinerary.flightOnlyItinerary.reduce(
            (carriers: string[], flightIngredientResponse) => {
              if (
                isAvailableTransportIngredientResponse(flightIngredientResponse)
              ) {
                carriers.push(...flightIngredientResponse.allCarriers);
              }

              return carriers;
            },
            []
          )
        ).filter((carrierCode) => !searchedCarrierNames.has(carrierCode));

        allCarrierCodes.forEach((carrierCode) =>
          searchedCarrierNames.add(carrierCode)
        );

        model.searchCarrierNames(allCarrierCodes).subscribe((carrierNames) => {
          tripState.insertCarrierNames(carrierNames);
        });
      },
      complete: () => {
        itineraryStore.itinerarySearchCounter--;
      },
    });

    combineLatest([result.itineraries, result.cheapestItinerary]).subscribe(
      ([itineraries, cheapestItinerary]: [
        itineraries: ItineraryWithPrice,
        cheapestItinerary: ItineraryWithPrice
      ]) =>
        setSuggestedNumberOfNigthsToRecipeFromItinerary(
          itineraries || cheapestItinerary
        )
    );

    function setSuggestedNumberOfNigthsToRecipeFromItinerary(
      itinerary: ItineraryWithPrice
    ): void {
      [
        ...itinerary.hotelOnlyItinerary,
        ...itinerary.ownStayOnlyItinerary,
      ].forEach((stayIngredientResponse) => {
        if (stayIngredientResponse.isNumberOfNightsSuggested) {
          const recipeStayIngredient = recipeState.getStayIngredient(
            stayIngredientResponse.ingredientId
          );
          if (!recipeStayIngredient) {
            return;
          }
          recipeStayIngredient.numberOfNights =
            stayIngredientResponse.numberOfNights;
        }
      });
    }

    result.cheapestItinerary
      .pipe(logged('cheapest itinerary'))
      .subscribe((itinerary) => (itineraryStore.cheapestItinerary = itinerary));

    result.staticInfo.subscribe({
      next: (overviewStaticInfoResult) => {
        tripState.insertOverviewStaticInfo(overviewStaticInfoResult);

        recipeState.hotelOnlyRecipe.value.forEach((hotelIngredient) => {
          updateDestinationAvailabilitySearchRange(hotelIngredient);
        });
      },
      complete: () => itineraryStore.staticItinerarySearchCounter--,
    });
  });

  function updateDestinationAvailabilitySearchRange(
    hotelIngredient: HotelIngredient
  ): void {
    const ingredientStaticInfo = Object.values(
      tripState.destinationStaticInfo.value
    ).filter(
      (destination) => destination.ingredientId === hotelIngredient.ingredientId
    );
    const uniqueResult =
      ingredientStaticInfo.length === 1 ? ingredientStaticInfo[0] : undefined;

    if (uniqueResult) {
      const staticInfo = tripState.getOverviewStaticInfo(
        uniqueResult.hotelCode,
        uniqueResult.ingredientId
      );

      const polygonFullName = staticInfo?.mainPolygon?.fullName;
      if (polygonFullName && hotelIngredient.searchText !== polygonFullName) {
        hotelIngredient.searchText = polygonFullName;
      }
      hotelIngredient.nearbyPolygonText = polygonFullName;
      hotelIngredient.commonPolygonText = polygonFullName;
    }
  }

  const throttleTimeMiliseconds = 500;
  asObservable(selectedItinerary)
    .pipe(
      throttleTime(throttleTimeMiliseconds, undefined, {
        leading: true,
        trailing: true,
      }),
      distinctUntilChanged(
        (previousItinerary, currentItinerary) =>
          previousItinerary?.key === currentItinerary?.key
      )
    )
    .subscribe((itinerary) => {
      if (!itinerary) {
        return;
      }

      const intineraryFlightCarrierCodes = uniq(
        itinerary.flightOnlyItinerary.reduce(
          (carrierCodes: string[], current: FlightIngredientResponse) => {
            let leg: TransportLeg | undefined;

            if (isAvailableFlightIngredientResponse(current)) {
              leg = current.legs[current.legIndex];
            } else if (current.alternativeSuggestion?.legs) {
              leg =
                current.alternativeSuggestion.legs[
                  current.alternativeSuggestion.legIndex
                ];
            } else {
              return [...carrierCodes];
            }

            assertValue(
              leg,
              'itineraryViewModel.ts > asObservable(selectedItinerary) > leg'
            );

            const segmentCarrier = leg.segments.flatMap((segment) => [
              segment.carrier,
              segment.operatingCarrier,
            ]);

            return [...carrierCodes, ...segmentCarrier];
          },
          []
        )
      );

      const intineraryFerryCarrierCodes = uniq(
        itinerary.ferryOnlyItinerary.reduce(
          (carriers: string[], current: FerryIngredientResponse) => {
            if (isAvailableTransportIngredientResponse(current)) {
              carriers.push(...current.carriers);
            }

            return carriers;
          },
          []
        )
      );

      model
        .searchCarrierStaticInfo([
          ...intineraryFlightCarrierCodes,
          ...intineraryFerryCarrierCodes,
        ])
        .subscribe(tripState.setCarrierStaticInfo);

      const itineraryAirportIatas = uniq(
        itinerary.flightOnlyItinerary.flatMap((flightIngredientResponse) => {
          if (
            isAvailableTransportIngredientResponse(flightIngredientResponse) &&
            flightIngredientResponse.legs
          ) {
            const leg =
              flightIngredientResponse.legs[flightIngredientResponse.legIndex];
            assertValue(
              leg,
              'itineraryViewModel.ts > itineraryAirportIatas > leg'
            );

            return flatIatas(leg.segments);
          } else if (flightIngredientResponse.alternativeSuggestion) {
            const leg =
              flightIngredientResponse.alternativeSuggestion.legs[
                flightIngredientResponse.alternativeSuggestion.legIndex
              ];
            assertValue(
              leg,
              'itineraryViewModel.ts > itineraryAirportIatas > alternative leg'
            );

            return flatIatas(leg.segments);
          }

          const ingredient = recipeState.getTransportIngredient(
            flightIngredientResponse.ingredientId
          );

          assertValue(
            ingredient.fromPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itineraryAirportIatas > fromPort'
          );
          assertValue(
            ingredient.toPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itineraryAirportIatas > toPort'
          );

          return [ingredient.fromPort, ingredient.toPort];
        })
      );

      const newAirportIatas = itineraryAirportIatas.filter(
        (iata) => !tripState.getPortStaticInfo(iata, IngredientType.Flight)
      );
      model
        .searchAirportStaticInfo(newAirportIatas)
        .subscribe((ports: Record<string, PortStaticInfo>) => {
          tripState.insertPortStaticInfo(ports, IngredientType.Flight);
          tripState.setFlightStaticInfoToRecipe();
        });

      // Ferries
      const itinerarySeaportIatas = itinerary.ferryOnlyItinerary.flatMap(
        (ferryIngredientResponse) => {
          if (isAvailableTransportIngredientResponse(ferryIngredientResponse)) {
            return [
              ferryIngredientResponse.arrivalIata,
              ferryIngredientResponse.departureIata,
            ];
          }

          const ingredient = recipeState.getTransportIngredient(
            ferryIngredientResponse.ingredientId
          );

          assertValue(
            ingredient.fromPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itinerarySeaportIatas > fromPort'
          );
          assertValue(
            ingredient.toPort,
            'ItineraryViewModel > asObservable(selectedItinerary) > itinerarySeaportIatas > toPort'
          );

          return [ingredient.fromPort, ingredient.toPort];
        }
      );
      const newSeaportIatas = itinerarySeaportIatas.filter(
        (iata) => !tripState.getPortStaticInfo(iata, IngredientType.Ferry)
      );
      tripState.isSearchingFerriesStaticInfo.value = true;
      model.searchSeaportStaticInfo(newSeaportIatas).subscribe({
        next: (ports: Record<string, PortStaticInfo>) => {
          tripState.insertPortStaticInfo(ports, IngredientType.Ferry);
          tripState.setFerryStaticInfoToRecipe();
        },
        complete: () => {
          tripState.isSearchingFerriesStaticInfo.value = false;
        },
        error: () => (tripState.isSearchingFerriesStaticInfo.value = false),
      });

      [
        ...itinerary.hotelOnlyItinerary,
        ...itinerary.ownStayOnlyItinerary,
      ].forEach((stayIngredientResponse) =>
        searchActivitiesAdditionalInfo(stayIngredientResponse)
      );
    });

  function searchActivitiesAdditionalInfo(
    stayIngredientResponse: HotelIngredientResponse | OwnStayIngredientResponse
  ): void {
    const parentIngredientId = stayIngredientResponse.ingredientId;

    stayIngredientResponse.subIngredientResponse.forEach((response) => {
      const activityIngredient = recipeState.getActivityIngredient(
        response.ingredientId
      );

      function ticketNeedsMoreInfo(ticketInfo: SelectedTicketInfo): boolean {
        return (
          ticketInfo.rate.value === undefined ||
          (ticketInfo.timeSlot !== undefined &&
            ticketInfo.timeSlot.value === undefined)
        );
      }

      if (
        !isAvailableActivityIngredientResponse(response) ||
        !activityIngredient.tickets.some(ticketNeedsMoreInfo)
      ) {
        return;
      }

      const productCode = response.suggested.code;
      const startDate = response.date;
      const endDate = response.date;

      model
        .searchActivityAvailability({
          userCriteria: {
            productCode,
            parentIngredientId,
            startDate,
            endDate,
            pax: criteriaState.itineraryCriteria.value.pax,
            typeOfProduct: IngredientType.Activity,
          },
          businessCriteria: criteriaState.businessCriteria.value,
        })
        .subscribe(([availability]) => {
          activityIngredient.tickets.forEach((ticketInfo) => {
            assertValue(
              availability,
              'itineraryViewModel.ts > model.searchActivityAvailability > availability'
            );

            const rateInfo = availability.rates.find(
              (rate) => rate.rateId === ticketInfo.rate.id
            );
            ticketInfo.rate.value = rateInfo?.title;

            if (!ticketInfo.timeSlot) {
              return;
            }

            const timeSlotInfo = rateInfo?.timeSlots?.find(
              (timeSlot) => timeSlot.timeSlotId === ticketInfo.timeSlot?.id
            );

            if (!timeSlotInfo) {
              return;
            }

            ticketInfo.timeSlot.value = getFormattedTime(
              timeSlotInfo.startTime
            );
          });
        });
    });
  }

  searchItinerariesFromCriteria(
    criteriaState.itineraryCriteria.value,
    criteriaState.businessCriteria.value,
    preferredTimeStore.preferredDay
  );
}
